import React, { useState, useEffect, useContext, ReactNode } from "react";
import queryString from "query-string";
import { useHistory, Redirect } from "react-router-dom";

import { Grid, IconButton, Divider, Button, Typography, Hidden, Tooltip } from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import CfdisPendingIcon from "@material-ui/icons/Receipt";
import CfdisArchiveIcon from "@material-ui/icons/Archive";
import CfdisValidatingIcon from "@material-ui/icons/AssignmentTurnedIn";
import CfdisWaitingIcon from "@material-ui/icons/AccountBalanceWallet";
import CfdisPendingCfdiIcon from "@material-ui/icons/NewReleases";
import CfdisCancelledIcon from "@material-ui/icons/RemoveCircle";
import WarningIcon from "@material-ui/icons/WarningTwoTone";
import moment from "moment";

import Pagination, { initialPage, initialPageSize, getOffset } from "../components/Pagination";
import { getCfdis, exportCfdis, downloadCfdis } from "../api/CfdiAPI";
import { getCfdis as getCustomerCfdis, exportCfdis as exportCustomerCfdis, downloadCfdis as downloadCustomerCfdis } from "../api/CustomerCfdiAPI";
import { Cfdi, Cfdis, CfdiQueryParams, TYPE as CfdiTypes, PAYMENT_STATUS as CfdiPaymentStatus, CfdisExportResponse } from "../model/Cfdi";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import ValidatedInput from "../components/ValidatedInput";
import CfdiMenu from "./CfdiMenu";
import Gridable, { GridableColumn } from "../components/Gridable";
import Ellipsis from "../components/Ellipsis";
import DateFormat from "../components/DateFormat";
import { RouterParams } from "../router/RouterParams";
import CfdisMenu from "./CfdisMenu";
import NewPaymentPopup from "../payments/NewPaymentPopup";
import { Payment } from "../model/Payment";
import { WarningSnackbar, SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import CurrencyFormat from "../components/CurrencyFormat";
import RegisterPaymentPopup from "../payments/RegisterPaymentPopup";
import ExportPopup from "../components/ExportPopup";
import { CustomerExportResponse } from "../model/Customer";
import CustomBackdrop from "../components/CustomBackdrop";
import CancelCfdiPopup from "./CancelCfdiPopup";
import ReplaceCfdiPopup from "./ReplaceCfdiPopup";
import MarkCfdiAsPaidPopup from "./MarkCfdiAsPaidPopup";
import DatePicker from "../components/DatePicker";

export function PendingCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisPendingIcon />} paymentStatus="pending" selectable />
    );
}

export function WaitingCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisWaitingIcon />} paymentStatus="waiting" selectable />
    );
}

export function ValidatingCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisValidatingIcon />} paymentStatus="validating" selectable />
    );
}

export function PendingCfdiCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisPendingCfdiIcon />} paymentStatus="pending_cfdi" selectable />
    );
}

export function CancelledCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisCancelledIcon />} paymentStatus="cancelled" selectable />
    );
}

export function AllCfdisList({ match }: RouterParams) {
    return (
        <CfdisList routeParams={match.params} icon={<CfdisArchiveIcon />} />
    );
}

interface CfdisListProps {
    routeParams: any;
    icon: ReactNode;
    paymentStatus?: string;
    selectable?: boolean;
}

type DownloadFormat = "excel" | "zip";

export function CfdisList(props: CfdisListProps) {
    const context = useContext(AppContext);
    const isCustomer = !!context.session?.customer?.id;
    const history = useHistory();
    const selectable = props.selectable;// && props.paymentStatus === "pending" && context.isGranted("CustomersCfdisCreate");
    const cfdiTypeLabels = CfdiTypes.map((type) => translate(`cfdis.tipo_comprobante.${type}`) as string);
    const cfdiStatusLabels = CfdiPaymentStatus.map((status) => translate(`cfdis.payment_status.${status}`) as string);
    const paramsFromQueryString = (): CfdiQueryParams => {
        const qs = queryString.parse(window.location.search);
        return {
            search: typeof qs["search"] === "string" ? qs["search"] as string : "",
            type: typeof qs["type"] === "string" ? qs["type"] as string : "",
            payment_status: props.paymentStatus || (typeof qs["payment_status"] === "string" ? qs["payment_status"] as string : ""),
            from: typeof qs["from"] === "string" ? qs["from"] as string : "",
            to: typeof qs["to"] === "string" ? qs["to"] as string : "",
        } as CfdiQueryParams;
    };

    const [status, setStatus] = useState<string>("loading");
    const [data, setData] = useState<Cfdis>();
    const [workingParams, setWorkingParams] = useState<CfdiQueryParams>(paramsFromQueryString);

    const [cfdi, setCfdi] = useState<Cfdi>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState<Cfdi[]>([]);
    const [selectedForPayment, setSelectedForPayment] = useState<Cfdi[]>();
    const [redirect, setRedirect] = useState<string>();
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();
    const [warning, setWarning] = useState<string>();
    const [dialog, setDialog] = useState<"register" | "exporting" | "cancel" | "replace" | "mark_as_paid">();
    const [downloadType, setDownloadType] = useState<DownloadFormat>();
    const [exportResponse, setExportResponse] = useState<CustomerExportResponse>();

    const retrievePromise = (): Promise<Cfdis> => {
        const params = paramsFromQueryString();
        setWorkingParams(params);

        const offset = getOffset(page, pageSize);
        let customerId = context.session?.customer?.id || props.routeParams.customerId;
        if (customerId) {
            return getCustomerCfdis(context.session!.tenant!.id, customerId, pageSize, offset, params);
        }
        return getCfdis(context.session!.tenant!.id, pageSize, offset, params);
    };

    const downloadPromise = (type: DownloadFormat, onlySelected?: boolean): Promise<CfdisExportResponse> => {
        let params: CfdiQueryParams;
        if (onlySelected && selected.length) {
            params = {
                search: "",
                type: "",
                ids: selected.map(cfdi => cfdi.id),
            } as CfdiQueryParams;
        } else {
            params = paramsFromQueryString();
        }

        let customerId = context.session?.customer?.id || props.routeParams.customerId;
        if (customerId) {
            if (type === "excel") {
                return exportCustomerCfdis(context.session!.tenant!.id, customerId, params);
            }
            return downloadCustomerCfdis(context.session!.tenant!.id, customerId, params);
        }

        if (type === "excel") {
            return exportCfdis(context.session!.tenant!.id, params);
        }
        return downloadCfdis(context.session!.tenant!.id, params);
    };

    const load = () => {
        setStatus("loading");
        retrievePromise().then((response) => {
            setData(response);
            setStatus("loaded");
        }).catch((error) => {
            setStatus(error.message);
        });
    };

    useEffect(load, [context.session, page, pageSize, window.location.search]);

    const onChangedPage = (page: number) => {
        setPage(page);
        setData(undefined);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
        setData(undefined);
    };

    const onChangedDate = (name: "from" | "to", raw?: string, date?: Date) => {
        setWorkingParams({ ...workingParams, [name]: raw || "" });
    };

    const onAppliedFilter = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        updateFilters(workingParams);
    };

    const updateFilters = (workingParams: CfdiQueryParams) => {
        let qs = queryString.parse(window.location.search);
        qs["search"] = workingParams.search;
        qs["type"] = workingParams.type;

        if (!props.paymentStatus && workingParams.payment_status) {
            qs["payment_status"] = workingParams.payment_status;
        } else {
            delete qs["payment_status"];
        }

        if (workingParams.from) {
            qs["from"] = workingParams.from;
        } else {
            delete qs["from"];
        }

        if (workingParams.to) {
            qs["to"] = workingParams.to;
        } else {
            delete qs["to"];
        }

        qs["page"] = "1";

        let url = window.location.pathname + "?" + queryString.stringify(qs);
        history.push(url);
    };

    const onFilterChanged = (name: string, value: string, inputRef: any) => {
        setWorkingParams({ ...workingParams, [name]: value });
    };

    const onClickedOptions = (cfdi: Cfdi) => (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCfdi(cfdi);
    };

    const onCloseOption = () => {
        setAnchorEl(null);
        setCfdi(undefined);
    };

    const onClickedMore = (event: React.MouseEvent<HTMLElement>) => {
        setGridAnchorEl(event.currentTarget);
    };

    const identifier = (cfdi: Cfdi) => {
        if (cfdi.serie || cfdi.folio) {
            return `${cfdi.serie || ""}${cfdi.serie ? "-" : ""}${cfdi.folio || ""}`;
        }
        if (cfdi.complementos && cfdi.complementos.timbre_fiscal_digital) {
            const uuid = cfdi.complementos.timbre_fiscal_digital.uuid;
            return uuid.substring(uuid.length - 10);
        }
        return "---";
    };

    const handleSelection = (ids: string[], cfdis: Cfdi[]) => {
        setSelected(cfdis);
    };

    const onNewPayment = () => {
        setGridAnchorEl(null);
        if (selected.length === 0) {
            setWarning(translate("payments.new.empty") as string);
            return;
        }

        if (selected.filter(cfdi => cfdi.tipo_comprobante !== "I").length > 0) {
            setWarning(translate("payments.new.only_income") as string);
            return;
        }

        setSelectedForPayment(selected);
    };

    const onNewPaymentSingle = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setSelectedForPayment([cfdi]);
    };

    const onRegisterPayment = () => {
        setGridAnchorEl(null);
        setDialog("register");
    };

    const onCancel = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setDialog("cancel");
    };

    const onCancelled = () => {
        setSuccess(translate("cfdis.cancel.success") as string);
        onCloseDialogs();
        load();
    };

    const onReplace = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setDialog("replace");
    };

    const onReplaced = () => {
        setSuccess(translate("cfdis.replace.success") as string);
        onCloseDialogs();
        load();
    };

    const onShowCustomerCfdis = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setRedirect(`/customers/${cfdi.metadata!.customer_id}/cfdis`);
    };

    const onGetBalance = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setDialog("exporting");
        exportCustomerCfdis(context.session!.tenant!.id, cfdi.metadata!.customer_id, {
            search: "",
            type: "I",
            payment_status: "pending"
        } as CfdiQueryParams).then((response) => {
            if (response.url) {
                setExportResponse(response);
            } else {
                setSuccess(translate("cfdis.download.async", { "total": response.total }) as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setDialog(undefined);
        });
    };

    const onMarkAsPaid = () => {
        setAnchorEl(null);
        if (!cfdi) return;

        setDialog("mark_as_paid");
    };

    const onMarkedAsPaid = () => {
        setSuccess(translate("cfdis.mark_as_paid.success") as string);
        onCloseDialogs();
        load();
    };

    const onCloseDialogs = () => {
        setSelectedForPayment(undefined);
        setDialog(undefined);
    };

    const showDownloadCfdis = (type: DownloadFormat, onlySelected?: boolean) => () => {
        setGridAnchorEl(null);
        setDialog("exporting");
        setDownloadType(type);
        downloadPromise(type, onlySelected).then((response) => {
            if (response.url) {
                setExportResponse(response);
            } else {
                setSuccess(translate("cfdis.download.async", { "total": response.total }) as string);
            }
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setDialog(undefined);
        });
    }

    const onSuccessNewPayment = (payment: Payment) => {
        setRedirect(`/payments/${payment.id}`);
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setWarning(undefined);
        setError(undefined);
    };

    const columns = () => {
        const items = [
            {
                title: translate("cfdis.id") as string,
                converter: (cfdi) => (
                    <>
                        <Grid container spacing={1} justify="space-between" alignContent="center" alignItems="center">
                            {cfdi.metadata?.cancel_in_process && (
                                <Grid item xs="auto">
                                    <Tooltip title={translate("cfdis.cancel_in_process") as string} placement="right-end" arrow>
                                        <WarningIcon fontSize="small" color="error" />
                                    </Tooltip>
                                </Grid>
                            )}
                            <Grid item xs>
                                <Ellipsis text={identifier(cfdi)} lenght={100} uppercased={false} />
                            </Grid>
                        </Grid>
                        <Ellipsis text={translate(`cfdis.tipo_comprobante.${cfdi.tipo_comprobante}`) as string} lenght={0} secondary />
                        {cfdi.metadata && cfdi.metadata.purchase_order && (
                            <Typography component="h6" variant="caption" color="textSecondary">
                                {cfdi.metadata.purchase_order}
                            </Typography>
                        )}
                        {cfdi.metadata && cfdi.metadata.sales_order && (
                            <Typography component="h6" variant="caption" color="textSecondary">
                                {cfdi.metadata.sales_order}
                            </Typography>
                        )}
                        {!props.paymentStatus && (
                            <Hidden lgUp>
                                <br />
                                <Typography variant="caption" component="small" color="textSecondary">
                                    <small>
                                        {cfdi.metadata && cfdi.metadata.payment_status ? translate(`cfdis.payment_status.${cfdi.metadata.payment_status}`) as string : "---"}
                                    </small>
                                </Typography>
                            </Hidden>
                        )}
                    </>
                ),
                xs: true
            },
            isCustomer ? undefined : {
                title: translate("customers.name"),
                converter: (cfdi) => (
                    <>
                        <Ellipsis text={cfdi.receptor.nombre} lenght={100} /><br />
                        <Ellipsis text={cfdi.receptor.rfc} lenght={0} secondary uppercased={false} />
                    </>
                ),
                xs: false,
                sm: false,
                md: 2,
                lg: props.paymentStatus ? 3 : 2,
                xl: 3,
            },
            {
                title: translate("cfdis.fecha") as string,
                converter: (cfdi) => (
                    <DateFormat date={cfdi.fecha} format="L" />
                ),
                xs: false,
                sm: 2,
                md: 2,
                lg: 2,
                xl: props.paymentStatus ? 2 : 1
            },
            {
                title: translate("cfdis.due_date") as string,
                converter: (cfdi) => (
                    cfdi.metadata && cfdi.metadata.credit && cfdi.metadata.credit.days ? <DateFormat date={moment(cfdi.fecha).add(cfdi.metadata.credit.days || 0, "d").toDate()} format="L" /> : "---"
                ),
                xs: false,
                sm: 2,
                md: 2,
                lg: 2,
                xl: props.paymentStatus ? 2 : 1
            },
            {
                title: translate("cfdis.total") as string,
                converter: (cfdi) => (
                    <CurrencyFormat amount={cfdi.total} currency={cfdi.moneda} />
                ),
                xs: false,
                sm: false,
                md: false,
                lg: isCustomer ? 2 : false,
                xl: isCustomer ? 2 : 1
            },
            {
                title: translate("cfdis.balance") as string,
                converter: (cfdi) => (
                    <>
                        <CurrencyFormat amount={cfdi.metadata?.balance || 0} currency={cfdi.moneda} />
                        <Hidden xlUp>
                            <br />
                            <Typography variant="caption" component="small" color="textSecondary">
                                <small>
                                    {translate("cfdis.total")}&nbsp;<CurrencyFormat amount={cfdi.total} currency={cfdi.moneda} />
                                </small>
                            </Typography>
                        </Hidden>
                    </>
                ),
                xs: 4,
                sm: 3,
                md: isCustomer ? 3 : 2,
                lg: 2,
                xl: isCustomer ? 2 : 1
            },
            props.paymentStatus ? undefined : {
                title: translate("cfdis.payment_status.title") as string,
                converter: (cfdi) => cfdi.metadata && cfdi.metadata.payment_status ? translate(`cfdis.payment_status.${cfdi.metadata.payment_status}`) as string : "---",
                xs: false,
                sm: 2,
                md: false,
                lg: isCustomer ? 2 : 1,
                xl: isCustomer ? 2 : 1
            },
            {
                title: (
                    <IconButton size="small" style={{ "visibility": "hidden" }} disabled><MoreVertIcon /></IconButton>
                ),
                converter: (cfdi) => (
                    <IconButton aria-label="options" color="default" size="small" onClick={onClickedOptions(cfdi)}
                        disabled={(selected.length === 1 && selected.findIndex(item => item.id === cfdi.id) < 0) || selected.length > 1}>
                        <MoreVertIcon />
                    </IconButton>
                ),
                justify: "flex-end",
                xs: "auto"
            }
        ] as GridableColumn<Cfdi>[];

        return items.filter(item => item !== undefined);
    };

    if (redirect) {
        return (
            <Redirect to={redirect} />
        );
    }

    return (
        <Pagination title={translate("cfdis.title")} subtitle={props.paymentStatus ? translate(`cfdis.payment_status.${props.paymentStatus}`) : undefined} icon={props.icon}
            page={page} pageSize={pageSize} count={data ? data.items.length : 0} total={data ? data.total : 0}
            onChangedPage={onChangedPage} onChangedPageSize={onChangedPageSize} action={(
                <Grid container alignItems="center" justify="flex-end" spacing={1}>
                    <Grid item xs="auto">
                        <IconButton color="default" size="small" onClick={onClickedMore}>
                            <MoreVertIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            )}>
            <form autoComplete="off" noValidate onSubmit={onAppliedFilter}>
                <Grid container alignItems="center" justify="flex-end" className="TableFilter" spacing={1}>
                    <Grid item xs={12} sm>
                        <ValidatedInput type="text" id="search" name="search" label={translate("cfdis.filter") as string}
                            margin="dense" disabled={false}
                            value={workingParams.search} onValueChanged={onFilterChanged} />
                    </Grid>
                    {!props.paymentStatus && (
                        <Grid item xs={12} sm="auto">
                            <ValidatedInput type="text" id="payment_status" name="payment_status" label={translate("cfdis.payment_status.title") as string}
                                options={CfdiPaymentStatus} optionLabels={cfdiStatusLabels}
                                margin="dense" disabled={false}
                                value={workingParams.payment_status} onValueChanged={onFilterChanged} />
                        </Grid>
                    )}
                    <Grid item xs={12} sm="auto">
                        <ValidatedInput type="text" id="type" name="type" label={translate("cfdis.tipo_comprobante.title") as string}
                            options={CfdiTypes} optionLabels={cfdiTypeLabels}
                            margin="dense" disabled={false}
                            value={workingParams.type} onValueChanged={onFilterChanged} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2} xl="auto" className="DatePicker">
                        <DatePicker label={translate("dates.from") as string} name="from" initial={workingParams.from} defaultIfMissing={false} onChange={onChangedDate} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2} xl="auto" className="DatePicker">
                        <DatePicker label={translate("dates.to") as string} name="to" initial={workingParams.to} defaultIfMissing={false} onChange={onChangedDate} />
                    </Grid>
                    <Grid item xs="auto">
                        <Button type="submit" variant="outlined" color="secondary" size="medium">
                            {translate("buttons.search")}
                        </Button>
                    </Grid>
                </Grid>
            </form>
            <Divider />
            <Gridable
                items={data ? data.items : []}
                loading={status === "loading"}
                error={status !== "loading" && status !== "loaded" ? status : undefined}
                empty={translate("cfdis.empty") as string}
                selectable={selectable}
                mapper={(cfdi) => cfdi.id}
                onChange={handleSelection}
                columns={columns()} />
            {cfdi && anchorEl &&
                <CfdiMenu anchor={anchorEl} cfdi={cfdi}
                    customerId={context.session?.customer?.id || props.routeParams.customerId}
                    onClose={onCloseOption}
                    onNewPayment={onNewPaymentSingle}
                    onCancel={onCancel}
                    onReplace={onReplace}
                    onShowCustomerCfdis={onShowCustomerCfdis}
                    onGetBalance={onGetBalance}
                    onMarkAsPaid={onMarkAsPaid} />
            }
            {gridAnchorEl && (
                <CfdisMenu anchor={gridAnchorEl} selected={selected.length}
                    totalItems={data ? data.items.length : 0}
                    paymentStatus={props.paymentStatus}
                    onClose={() => setGridAnchorEl(null)}
                    onNewPayment={onNewPayment}
                    onRegisterPayment={onRegisterPayment}
                    onExportCfdis={showDownloadCfdis("excel")}
                    onDownloadCfdis={showDownloadCfdis("zip")}
                    onDownloadSelectedCfdis={showDownloadCfdis("zip", true)} />
            )}
            {selectedForPayment && (
                <NewPaymentPopup cfdis={selectedForPayment} onClose={onCloseDialogs} onSuccess={onSuccessNewPayment} />
            )}
            {dialog === "register" && (
                <RegisterPaymentPopup onClose={onCloseDialogs} />
            )}
            {dialog === "exporting" && (
                <CustomBackdrop open />
            )}
            {dialog === "cancel" && cfdi && (
                <CancelCfdiPopup cfdi={cfdi} onSuccess={onCancelled} onClose={onCloseDialogs} />
            )}
            {dialog === "replace" && cfdi && (
                <ReplaceCfdiPopup cfdi={cfdi} onSuccess={onReplaced} onClose={onCloseDialogs} />
            )}
            {dialog === "mark_as_paid" && cfdi && (
                <MarkCfdiAsPaidPopup cfdi={cfdi} onSuccess={onMarkedAsPaid} onClose={onCloseDialogs} />
            )}
            {exportResponse && exportResponse.url && (
                <ExportPopup
                    title={translate("cfdis.download.title") as string}
                    message={translate(`cfdis.download.${downloadType}`, { "total": exportResponse.total }) as string}
                    url={exportResponse.url}
                    onClose={() => setExportResponse(undefined)} />
            )}
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <WarningSnackbar message={warning} onClose={onCloseSnackbars} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
        </Pagination >
    );
}
